import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuappBySlug } from '../../hooks/useQuappBySlug'
import { useQuappHomeScreenMeta } from '../../hooks/useQuappHomeScreenMeta'
import { useAppConfig } from '../../hooks/useAppConfig'
import { useBridgeManager } from '../../hooks/useBridgeManager'
import { MobileFrame } from './components/MobileFrame'
import { InstallLanding } from './components/InstallLanding'
import { LoadingState } from './components/LoadingState'
import { ErrorState } from './components/ErrorState'

/**
 * Web Frame Page — route /quapps/:slug
 *
 * Product model: a Quapp URL in a *browser* is purely an INSTALLER. The Quapp
 * itself is never rendered in the browser — it only runs once installed and
 * launched from the home screen (display-mode: standalone).
 *
 *  - Browser (not installed)  -> <InstallLanding> (install button / instructions)
 *  - Installed (standalone)   -> <MobileFrame> (the real Quapp, full-bleed)
 */
export function WebFramePage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: quapp, isLoading, error } = useQuappBySlug(slug)
  const { data: appConfig } = useAppConfig()

  // Tab title, icons, per-Quapp manifest + toolbar color.
  useQuappHomeScreenMeta(quapp)

  const isStandalone = useMemo(
    () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true,
    []
  )

  // Bridge manager (only meaningful once the Quapp actually runs, i.e. standalone)
  const { bridgeManager, showInstallPrompt, promptFeature, closeInstallPrompt } = useBridgeManager({
    storeUrls: appConfig?.storeUrls,
    promptEnabled: appConfig?.promptEnabled ?? true,
  })

  if (isLoading) {
    return <LoadingState slug={slug} />
  }

  if (error || !quapp) {
    const errorType =
      error instanceof Error && error.message === 'NOT_FOUND' ? 'NOT_FOUND' : 'UNKNOWN'
    return <ErrorState type={errorType} slug={slug} />
  }

  // In a browser the URL is an installer only — never render the Quapp.
  if (!isStandalone) {
    return <InstallLanding quapp={quapp} />
  }

  // Installed app: the real Quapp, full-bleed.
  return (
    <MobileFrame
      quapp={quapp}
      bridgeManager={bridgeManager}
      showBridgeOverlay={showInstallPrompt}
      bridgeFeature={promptFeature}
      storeUrls={appConfig?.storeUrls || { android: null, ios: null }}
      onCloseBridgeOverlay={closeInstallPrompt}
    />
  )
}
